import { Field, Form, Formik } from "formik";
import { useRef, useState } from "react";
import { Form as BForm, Card, Col, Row, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import * as Yup from "yup";
import TextInput from "../../../../../components/formfields/InputField";
import ColorPicker, { useColorPicker } from "react-best-gradient-color-picker";
import AnimatedWords from "./AnimatedWords";
import { useDispatch, useSelector } from "react-redux";
import { addHomeDataAction } from "../actions";
import useClickOutside from "../../../../../hooks/useClickOutside";
import UploadVideo from "./UploadVideo";

const heroSchema = Yup.object({
  hero_banner_video_link_youtube: Yup.string()
    .url("Must be a valid url")
    .nullable()
    .test("custom-validation", "Invalid video URL", (value: any): any => {
      const videoUrlPattern = /^(http(s)?:\/\/)?((w){3}.)?(m\.)?((youtube\.com\/watch\?v=)|(youtu.be\/))([^\s&]+)$/;
      console.log("value", value);
      if (!value || videoUrlPattern.test(value || "")) return true;
      return new Promise((res) => {
        const video = document.createElement("video");
        video.setAttribute("src", value);
        video.addEventListener("canplay", function () {
          console.log("video true");
          res(true);
        });
        video.addEventListener("error", function () {
          console.log("video false");
          res(false);
        });
      });
    }),
});

const HeroSection = ({ props }: any) => {
  const [heroEdit, setHeroEdit] = useState<boolean>(false);
  const [animatedWord, setAnimatedWord] = useState<string>("");
  const [showPicker, setShowPicker] = useState<boolean>(false);
  const [videoUploading, setVideoUploading] = useState(false);
  const [color, setColor] = useState<string>("linear-gradient(140deg, rgb(198, 255, 221) 0%, rgb(251, 215, 134) 59%, rgb(247, 121, 125) 100%)");
  useColorPicker(color, setColor);
  const hero_state = useSelector((state: any) => state?.home?.home_data);
  const dispatch = useDispatch();
  const myRef = useRef(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      buttonRef?.current?.click();
    }
  };

  const handleClickOutside = () => {
    setShowPicker(false);
    // handle outside click here
  };
  useClickOutside(myRef, handleClickOutside);
  const heroState = {
    hero_animated_words: hero_state?.hero_animated_words ? hero_state?.hero_animated_words : [],
    hero_constant_label: hero_state?.hero_constant_label ? hero_state?.hero_constant_label : "",
    hero_header_discription: hero_state?.hero_header_discription ? hero_state?.hero_header_discription : "",
    hero_tax_tagline: hero_state?.hero_tax_tagline ? hero_state?.hero_tax_tagline : "",
    hero_video1: hero_state?.hero_video1 ? hero_state?.hero_video1 : "",
    hero_video2: hero_state?.hero_video2 ? hero_state?.hero_video2 : "",
    hero_video3: hero_state?.hero_video3 ? hero_state?.hero_video3 : "",
    hero_banner_video: hero_state?.hero_banner_video ? hero_state?.hero_banner_video : "",
    hero_banner_video_link: hero_state?.hero_banner_video_link === "true" ? hero_state?.hero_banner_video_link : "",
    hero_banner_video_link_youtube: hero_state?.hero_banner_video_link_youtube ? hero_state?.hero_banner_video_link_youtube : "",
  };

  const handleHeroSubmit = (values: any) => {
    if (!videoUploading) {
      const form_data = new FormData();
      form_data.append("id", "hero");
      form_data.append("hero_animated_words", JSON.stringify(values.hero_animated_words));
      form_data.append("hero_constant_label", values.hero_constant_label);
      form_data.append("hero_header_discription", values.hero_header_discription);
      form_data.append("hero_tax_tagline", values.hero_tax_tagline);
      form_data.append("hero_video1", values.hero_video1);
      form_data.append("hero_video2", values.hero_video2);
      form_data.append("hero_video3", values.hero_video3);
      form_data.append("hero_banner_video", values.hero_banner_video_link !== "true" ? values.hero_banner_video : "");
      form_data.append("hero_banner_video_link", values.hero_banner_video_link);
      form_data.append("hero_banner_video_link_youtube", values.hero_banner_video_link === "true" ? values.hero_banner_video_link_youtube : "");
      dispatch(addHomeDataAction(form_data, setHeroEdit, "add-hero-section"));
      setHeroEdit(false);
      console.log("values_hero", values);
    } else {
      toast.warning("Video upload in progress");
    }
  };

  const handleAddAnimatedWord = (setFieldValue: any, values: any) => {
    if (animatedWord.trim().length) {
      setFieldValue("hero_animated_words", [...values.hero_animated_words, { name: animatedWord, style: color }]);
      setAnimatedWord("");
      setShowPicker(false);
    } else {
      toast.error("Can't add empty animated word");
    }
  };

  const handleAddLinkOrUpload = (setField: any, link: boolean) => {
    if (link) {
      setField("hero_banner_video_link", "true");
      setField("hero_banner_video", "");
    } else {
      setField("hero_banner_video_link", "");
      setField("hero_banner_video_link_youtube", "");
    }
  };

  return (
    <Formik enableReinitialize initialValues={heroState} validationSchema={heroSchema} onSubmit={handleHeroSubmit}>
      {({ values, handleChange, handleBlur, setFieldValue, errors }: any) => {
        return (
          <Form
            onKeyPress={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
              }
            }}
          >
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h6 className={`mb-0 ${heroEdit ? "editTitle" : ""}`}>Hero Section</h6>
                {heroEdit ? (
                  <button className="btn btn-primary" type="submit">
                    <i className="fa-regular fa-floppy-disk"></i> Save
                  </button>
                ) : (
                  <span className="btn btn-green" onClick={() => setHeroEdit(true)}>
                    <i className="fa-regular fa-pen-to-square"></i> Edit
                  </span>
                )}
              </Card.Header>
              <Card.Body className="position-relative">
                <div
                  className={`position-absolute w-100 h-100 start-0 top-0 zIndex-1 ${heroEdit ? "d-none" : ""}`}
                  style={{ background: "rgba(255,255,255,0.5)", cursor: "not-allowed" }}
                ></div>
                <Row className="gy-3">
                  <Col lg="6">
                    <BForm.Label>Animated Word</BForm.Label>
                    <BForm.Control
                      className="form-control"
                      type="text"
                      readOnly={!heroEdit}
                      value={animatedWord}
                      onChange={(e) => e.target.value.length < 19 && setAnimatedWord(e.target.value)}
                      onKeyUp={handleKeyPress}
                    ></BForm.Control>
                  </Col>
                  <Col lg="6">
                    <BForm.Label>Color Picker</BForm.Label>
                    <div className="position-relative colorPickerInput">
                      <label className="colorLabel" onClick={() => heroEdit && !showPicker && setShowPicker(true)} style={{ background: `${color}` }}></label>

                      <BForm.Control className="form-control-" type="text" disabled></BForm.Control>
                      <Button className="addBtn" ref={buttonRef} onClick={() => heroEdit && handleAddAnimatedWord(setFieldValue, values)}>
                        <i className="fa-regular fa-plus"></i>
                      </Button>

                      {showPicker && heroEdit && (
                        <div className="colorPickerBox mt-5" ref={myRef}>
                          <ColorPicker value={color} onChange={setColor} />
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col xs="12">
                    <AnimatedWords edit={heroEdit} list={values.hero_animated_words} setFieldValue={setFieldValue} />
                    {errors.hero_animated_words && <p className="errorMsg">{errors.hero_animated_words}</p>}
                  </Col>
                  <Col xs="12">
                    <BForm.Label>Constant label</BForm.Label>
                    <Field
                      name="hero_constant_label"
                      component={TextInput}
                      value={values.hero_constant_label}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Constant label"
                      readOnly={!heroEdit}
                      className={heroEdit ? "editField" : ""}
                    />
                  </Col>
                  <Col xs="12">
                    <BForm.Label>Header Description</BForm.Label>
                    <Field
                      name="hero_header_discription"
                      component={TextInput}
                      value={values.hero_header_discription}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Header Description"
                      readOnly={!heroEdit}
                      className={heroEdit ? "editField" : ""}
                      multiline
                    />
                  </Col>
                  <Col xs="12">
                    <BForm.Label>Tax Tagline</BForm.Label>
                    <Field
                      name="hero_tax_tagline"
                      component={TextInput}
                      value={values.hero_tax_tagline}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Tax tagline"
                      readOnly={!heroEdit}
                      className={heroEdit ? "editField" : ""}
                    />
                  </Col>
                  <Col xs="12">
                    <label className={`form-label ${!heroEdit ? "edit" : ""} `} htmlFor="image">
                      Upload the demo Videos
                    </label>
                    <Row className="gy-4">
                      <Col xs="12">
                        <UploadVideo
                          key="hero_video1"
                          name="hero_video1"
                          value={values.hero_video1}
                          setFieldValue={setFieldValue}
                          edit={heroEdit}
                          uploadAction={props.uploadFileAction}
                          count={1}
                          setVideoUploading={setVideoUploading}
                        />
                      </Col>
                      <Col xs="12">
                        <UploadVideo
                          key="hero_video2"
                          name="hero_video2"
                          value={values.hero_video2}
                          setFieldValue={setFieldValue}
                          edit={heroEdit}
                          uploadAction={props.uploadFileAction}
                          count={2}
                          setVideoUploading={setVideoUploading}
                        />
                      </Col>
                      <Col xs="12">
                        <UploadVideo
                          key="hero_video3"
                          name="hero_video3"
                          value={values.hero_video3}
                          setFieldValue={setFieldValue}
                          edit={heroEdit}
                          uploadAction={props.uploadFileAction}
                          count={3}
                          setVideoUploading={setVideoUploading}
                        />
                      </Col>
                    </Row>
                  </Col>
                  <Col xs="12">
                    <BForm.Label>Banner Video</BForm.Label>
                    <Row className="gy-1">
                      <Col sm="auto">
                        <BForm.Check
                          id="no"
                          type="radio"
                          label="Upload Banner Video"
                          name="hero_banner_video_link"
                          checked={values.hero_banner_video_link === ""}
                          value=""
                          readOnly={!heroEdit}
                          disabled={!heroEdit}
                          onChange={() => handleAddLinkOrUpload(setFieldValue, false)}
                        />
                      </Col>
                      <Col sm="auto">
                        <BForm.Check
                          id="yes"
                          type="radio"
                          label="Add Banner Video Link"
                          name="hero_banner_video_link"
                          checked={values.hero_banner_video_link === "true"}
                          value="true"
                          readOnly={!heroEdit}
                          disabled={!heroEdit}
                          onChange={() => handleAddLinkOrUpload(setFieldValue, true)}
                        />
                      </Col>
                    </Row>
                    {values.hero_banner_video_link === "true" ? (
                      <Row>
                        <Col xs="12" className="mt-3">
                          <BForm.Label>Banner Video Link</BForm.Label>
                          <Field
                            name="hero_banner_video_link_youtube"
                            component={TextInput}
                            value={values.hero_banner_video_link_youtube}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            placeholder="Banner video youtube link"
                            readOnly={!heroEdit}
                            className={heroEdit ? "editField" : ""}
                          />
                        </Col>
                      </Row>
                    ) : (
                      <Row>
                        <Col xs="12" className="mt-3">
                          <label className={`form-label mb-0 ${!heroEdit ? "edit" : ""}`} htmlFor="image">
                            Upload the banner Videos
                          </label>
                          <UploadVideo
                            name="hero_banner_video"
                            value={values.hero_banner_video}
                            setFieldValue={setFieldValue}
                            edit={heroEdit}
                            uploadAction={props.uploadFileAction}
                            setVideoUploading={setVideoUploading}
                          />
                        </Col>
                      </Row>
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Form>
        );
      }}
    </Formik>
  );
};

export default HeroSection;
